import { jsxs, jsx } from "react/jsx-runtime";
import { cn } from "../../../lib/utils.js";
import "../shared/quiz-blank.js";
import { KatexText } from "../shared/katex-text.js";
import { QuizResultFeedback } from "../shared/quiz-result-feedback.js";
import { useTranslation } from "react-i18next";
import "../shared/quiz-style-context.js";
import "react";
import { SubmitButton } from "../shared/submit-button.js";
import "../../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import { QuizMedia } from "../shared/quiz-media.js";
import { QuizTypeBadge } from "../shared/quiz-type-badge.js";
import { QuizSolveDescription } from "../shared/quiz-solve-description.js";
import "../../../context/DevToolsContext.js";
import { formatText } from "../../../utils/quiz-parser.js";
import { useCountingQuiz } from "../hooks/use-counting-quiz.js";
import { motion } from "../../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/render/components/motion/proxy.js";
import ChevronUp from "../../../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/chevron-up.js";
import ChevronDown from "../../../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/chevron-down.js";
function CountingAdult({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel,
  hideExplanation = false
}) {
  const { t } = useTranslation("quiz");
  const {
    displayValues,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    correctDigits,
    isDisabled,
    handleUp,
    handleDown,
    handleSubmit
  } = useCountingQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange
  });
  return /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    /* @__PURE__ */ jsx(
      motion.div,
      {
        initial: { opacity: 0, y: -10 },
        animate: { opacity: 1, y: 0 },
        className: "space-y-2",
        children: /* @__PURE__ */ jsx(
          QuizTypeBadge,
          {
            quizId,
            typeLabel: t("types.counting.label"),
            difficultyLevel
          }
        )
      }
    ),
    /* @__PURE__ */ jsxs(
      motion.div,
      {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        transition: { delay: 0.1 },
        className: cn(
          "p-6 bg-linear-to-br to-gray-50 rounded-2xl from-slate-50",
          "border border-gray-100 shadow-2xs",
          "text-2xl font-medium leading-relaxed text-gray-900"
        ),
        children: [
          /* @__PURE__ */ jsx(KatexText, { text: quiz.title }),
          quiz.problem && (() => {
            const cleanProblem = quiz.problem.replace(/\{\{\}\}/g, "").trim();
            if (!cleanProblem) return null;
            return /* @__PURE__ */ jsx("div", { className: "mt-4 text-xl", children: formatText(cleanProblem, `${quizId}-problem`, quiz.additionalImages ?? void 0) });
          })(),
          /* @__PURE__ */ jsx(QuizMedia, { image: quiz.image, imageMobile: quiz.imageMobile, desmos: quiz.desmos, vars: quiz.vars })
        ]
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center gap-4 mt-6", children: displayValues.map((value, index) => /* @__PURE__ */ jsxs(
      motion.div,
      {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        transition: { delay: index * 0.05 },
        className: cn(
          "inline-flex flex-col items-center rounded-xl border-2 overflow-hidden",
          shouldShowCorrectAnswers || isSolved && (solveResult == null ? void 0 : solveResult.correct) ? "border-green-300 bg-green-50" : isSolved && !(solveResult == null ? void 0 : solveResult.correct) ? "border-red-300 bg-red-50" : "border-gray-300 bg-white"
        ),
        children: [
          /* @__PURE__ */ jsx(
            "button",
            {
              onClick: () => handleUp(index),
              disabled: isDisabled,
              className: cn(
                "w-16 h-10 flex items-center justify-center transition-colors",
                !isDisabled ? "hover:bg-blue-50 text-blue-500 active:bg-blue-100" : "text-gray-300 cursor-not-allowed"
              ),
              "aria-label": t("types.counting.incrementDigit", { position: index + 1 }),
              children: /* @__PURE__ */ jsx(ChevronUp, { className: "w-6 h-6" })
            }
          ),
          /* @__PURE__ */ jsx("div", { className: cn(
            "w-16 h-16 flex items-center justify-center text-4xl font-semibold border-y",
            shouldShowCorrectAnswers || isSolved && (solveResult == null ? void 0 : solveResult.correct) ? "text-green-600 border-green-200" : isSolved && !(solveResult == null ? void 0 : solveResult.correct) ? "text-red-500 border-red-200" : "text-gray-800 border-gray-200"
          ), children: value }),
          /* @__PURE__ */ jsx(
            "button",
            {
              onClick: () => handleDown(index),
              disabled: isDisabled,
              className: cn(
                "w-16 h-10 flex items-center justify-center transition-colors",
                !isDisabled ? "hover:bg-blue-50 text-blue-500 active:bg-blue-100" : "text-gray-300 cursor-not-allowed"
              ),
              "aria-label": t("types.counting.decrementDigit", { position: index + 1 }),
              children: /* @__PURE__ */ jsx(ChevronDown, { className: "w-6 h-6" })
            }
          )
        ]
      },
      index
    )) }),
    !isSolved && !isViewSolved && !hideSubmitButton && /* @__PURE__ */ jsx(
      SubmitButton,
      {
        onClick: handleSubmit,
        disabled: !canSubmit,
        label: submitLabel
      }
    ),
    isSolved && /* @__PURE__ */ jsx(
      QuizResultFeedback,
      {
        isCorrect: (solveResult == null ? void 0 : solveResult.correct) ?? false,
        show: isSolved,
        correctAnswer: shouldShowCorrectAnswers && correctDigits ? correctDigits.join("") : void 0,
        disableSound,
        isViewSolved
      }
    ),
    isSolved && (solveResult == null ? void 0 : solveResult.description) && !hideExplanation && /* @__PURE__ */ jsx(
      QuizSolveDescription,
      {
        description: solveResult.description,
        commentaryImages: quiz.commentaryImages,
        vars: quiz.vars,
        id: `${quizId}-desc`,
        actionVisible: !solveResult.correct,
        actionLabel: isShowingCorrectAnswers ? t("common.viewMyAnswer") : t("common.viewCorrectAnswer"),
        onActionClick: () => setIsShowingCorrectAnswers((v) => !v)
      }
    )
  ] });
}
export {
  CountingAdult
};
//# sourceMappingURL=counting-adult.js.map
